/**
 * ContractDenseForm — F-003.3 Phase 1 (IMPL-P1-005) + F-003.4 Phase 4 (IMPL-P4-008).
 * Shell del form denso per AGENTE/COMMERCIALISTA.
 * Riusa useContractForm (F-003.2) senza modifiche.
 * Accordion type="multiple" con DenseSection per ogni blocco.
 *
 * F-003.4: il vecchio CTA "Procedi al caricamento PDF" è stato sostituito da
 * `DenseSubmitFooter`, che integra PDFDropzone (compact) + consenso + submit
 * direttamente. La prop legacy `onProceedToPDF` è mantenuta opzionale per
 * back-compat con test/callsite legacy ma non è più usata in produzione.
 */
import { useState } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { useContractForm } from '../../hooks/useContractForm';
import { SaveDraftIndicator } from '../wizard/SaveDraftIndicator';
import { LocatoreFields } from '../fields/LocatoreFields';
import { ConduttoreFields } from '../fields/ConduttoreFields';
import { ImmobileFields } from '../fields/ImmobileFields';
import { CanoneFields } from '../fields/CanoneFields';
import { DurataFields } from '../fields/DurataFields';
import { IBANInput } from '@/features/profilo/components/IBANInput';
import { Accordion } from '@/components/ui/accordion';
import { DenseSection } from './DenseSection';
import { DenseErrorSummary } from './DenseErrorSummary';
import { DenseSubmitFooter } from './DenseSubmitFooter';
import { DENSE_SECTIONS } from '../../lib/dense-sections';
import type { DenseSectionId } from '../../lib/dense-sections';
import type { PraticaView } from '../../lib/pratica-client';

export interface ContractDenseFormProps {
  praticaId: string;
  /** F-003.4: chiamata dopo submit riuscita (es. navigate alla detail page). */
  onSubmitted?: (pratica: PraticaView) => void;
  /**
   * @deprecated F-003.4: il dense form ora integra il submit direttamente
   * tramite DenseSubmitFooter. Prop legacy mantenuta per back-compat.
   */
  onProceedToPDF?: () => void;
}

export function ContractDenseForm({
  praticaId,
  onSubmitted,
}: ContractDenseFormProps) {
  const { form, draftStatus } = useContractForm(praticaId);
  const [submitAttempted] = useState(false);
  const [openSections, setOpenSections] = useState<string[]>(['locatore']);

  const { formState } = form;

  const handleJumpTo = (sectionId: DenseSectionId) => {
    // Apri la sezione target se non e gia aperta
    setOpenSections((prev) => [...new Set([...prev, sectionId])]);
    // Sposta focus sul trigger della sezione
    setTimeout(() => {
      document.getElementById(`section-${sectionId}-trigger`)?.focus();
    }, 50);
  };

  return (
    <div
      data-testid="contract-dense-form"
      data-pratica-id={praticaId}
      className="space-y-4"
    >
      {/* Indicatore autosave */}
      <SaveDraftIndicator status={draftStatus} />

      {/* Riepilogo errori (visibile solo dopo primo tentativo submit) */}
      <DenseErrorSummary
        errors={formState.errors}
        onJumpTo={handleJumpTo}
        visible={submitAttempted}
      />

      <FormProvider {...form}>
        <Accordion
          type="multiple"
          value={openSections}
          onValueChange={setOpenSections}
          className="space-y-2"
        >
          {DENSE_SECTIONS.map((section) => (
            <DenseSection
              key={section.id}
              id={section.id}
              title={section.title}
              fields={section.fields}
              isOpen={openSections.includes(section.id)}
            >
              {section.id === 'locatore' && <LocatoreFields />}
              {section.id === 'conduttore' && <ConduttoreFields />}
              {section.id === 'immobile' && <ImmobileFields />}
              {section.id === 'termini' && (
                <div className="space-y-4">
                  <CanoneFields />
                  <DurataFields />
                </div>
              )}
              {section.id === 'iban' && (
                <Controller
                  name="ibanAdE"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <IBANInput
                      id="dense-iban-ade"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              )}
            </DenseSection>
          ))}
        </Accordion>
      </FormProvider>

      {/* F-003.4: Footer sticky con PDFDropzone + consenso + submit */}
      <DenseSubmitFooter
        praticaId={praticaId}
        onSubmitted={(pratica) => onSubmitted?.(pratica)}
      />
    </div>
  );
}
